import React, { useEffect, useState } from "react";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { api } from "@/api";
import { useNavigate } from "react-router-dom";

const roleLabel = (r) => (r === "owner" ? "Propriétaire" : r === "superadmin" ? "Admin" : "Pro");

const AccountRow = ({ u, meId, onToggle, onSwitch, onRemove, onTransfer }) => (
  <div data-testid={`account-row-${u.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white p-4">
    <div className="min-w-[200px] flex-1">
      <p className="text-[14px] font-semibold text-ink">{u.email}{u.id === meId ? " (vous)" : ""}</p>
      <p className="text-[12px] text-mute">{u.company?.company_name || "—"} · {roleLabel(u.role)}{u.active ? "" : " · désactivé"}</p>
    </div>
    {u.role !== "owner" && (
      <>
        <button data-testid={`account-toggle-${u.id}`} onClick={() => onToggle(u)}
          className={`rounded-full px-3 py-1.5 text-[13px] font-semibold ${u.active ? "bg-ok/15 text-ok" : "bg-paper-3 text-ink-2"}`}>
          {u.active ? "Actif" : "Inactif"}
        </button>
        <button data-testid={`account-role-${u.id}`} onClick={() => onSwitch(u)}
          className="rounded-full bg-paper-3 px-3 py-1.5 text-[13px] font-medium text-ink-2">
          {u.role === "superadmin" ? "→ Pro" : "→ Admin"}
        </button>
        {u.role === "superadmin" && u.active && (
          <button data-testid={`account-transfer-${u.id}`} onClick={() => onTransfer(u)}
            className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-[13px] font-semibold text-white">
            <Icon name="shield" size={14} /> Transférer la propriété
          </button>
        )}
        <button data-testid={`account-delete-${u.id}`} onClick={() => onRemove(u)}
          className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium text-err">
          <Icon name="trash-2" size={15} /> Supprimer
        </button>
      </>
    )}
    {u.role === "owner" && u.id !== meId && (
      <span className="rounded-full bg-ink/10 px-3 py-1.5 text-[13px] font-semibold text-ink">Propriétaire actuel</span>
    )}
  </div>
);

export default function RootAccounts() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [meId, setMeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("pro");
  const [company, setCompany] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, me] = await Promise.all([api.listUsers(), api.me()]);
      setUsers(u.users || []);
      setMeId(me.user?.id || "");
      setError("");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  const addAccount = async () => {
    if (!email.trim() || password.length < 6) { setError("Email et mot de passe (min. 6) requis."); return; }
    setAdding(true); setError("");
    try {
      const body = { email: email.trim(), password, company_name: company.trim() || undefined };
      if (role === "superadmin") body.role = "superadmin";
      await api.createUser(body);
      setEmail(""); setPassword(""); setCompany(""); setRole("pro");
      flash("Compte créé.");
      await load();
    } catch (e) { setError(e.message); } finally { setAdding(false); }
  };

  const toggleActive = async (u) => {
    try { await api.updateUser(u.id, { active: !u.active }); await load(); }
    catch (e) { setError(e.message); }
  };
  const switchRole = async (u) => {
    const next = u.role === "superadmin" ? "pro" : "superadmin";
    if (!window.confirm(`Changer le rôle de ${u.email} en « ${roleLabel(next)} » ?`)) return;
    try { await api.updateUser(u.id, { role: next }); await load(); flash("Rôle mis à jour."); }
    catch (e) { setError(e.message); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte ${u.email} ?`)) return;
    try { await api.deleteUser(u.id); await load(); flash("Compte supprimé."); }
    catch (e) { setError(e.message); }
  };
  const transfer = async (u) => {
    if (!window.confirm(`Transférer la PROPRIÉTÉ de l'application à ${u.email} ?\n\nVous perdrez l'accès super-admin racine et deviendrez un admin normal.`)) return;
    try {
      await api.transferOwnership(u.id);
      alert(`La propriété a été transférée à ${u.email}. Vous allez être déconnecté.`);
      api.adminLogout();
      navigate("/root", { replace: true });
    } catch (e) { setError(e.message); }
  };

  const admins = users.filter((u) => u.role === "owner" || u.role === "superadmin");
  const pros = users.filter((u) => u.role === "pro");

  return (
    <div className="mx-auto max-w-4xl px-5 py-8">
      <h1 className="font-display text-4xl font-semibold text-ink">Comptes</h1>
      <p className="mt-1 text-[14px] text-mute">{"Gérez les comptes admin (comme Déco Ligot) et pro, et transférez la propriété de l'application."}</p>

      {error ? <p data-testid="accounts-error" className="mt-4 text-[13px] text-err">{error}</p> : null}
      {msg ? <p data-testid="accounts-msg" className="mt-4 text-[13px] text-ok">{msg}</p> : null}

      <div className="mt-6 rounded-2xl border border-line bg-white p-5" data-testid="account-add-panel">
        <h3 className="mb-3 text-[16px] font-semibold text-ink">Créer un compte</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Email" testId="new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemple.com" className="mb-0" />
          <Field label="Mot de passe" testId="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min. 6 caractères" className="mb-0" />
          <Field label="Nom société (optionnel)" testId="new-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Ex : Déco Ligot" className="mb-0" />
          <div>
            <label className="mb-1 block text-[13px] font-medium text-ink-3">Rôle</label>
            <select data-testid="new-role" value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-[15px] text-ink outline-none focus:border-brand">
              <option value="pro">Pro</option>
              <option value="superadmin">Admin (comme Déco Ligot)</option>
            </select>
          </div>
        </div>
        <div className="mt-4"><PrimaryButton testId="account-add" title={adding ? "Création…" : "Créer le compte"} icon="user-plus" onClick={addAccount} /></div>
      </div>

      {loading ? (
        <p className="mt-6 text-[14px] text-mute">Chargement…</p>
      ) : (
        <>
          <h2 className="mt-8 mb-3 text-[15px] font-semibold uppercase tracking-wide text-ink-3">Admins</h2>
          <div className="space-y-3">{admins.map((u) => <AccountRow key={u.id} u={u} meId={meId} onToggle={toggleActive} onSwitch={switchRole} onRemove={remove} onTransfer={transfer} />)}</div>
          <h2 className="mt-8 mb-3 text-[15px] font-semibold uppercase tracking-wide text-ink-3">Professionnels ({pros.length})</h2>
          <div className="space-y-3">{pros.length ? pros.map((u) => <AccountRow key={u.id} u={u} meId={meId} onToggle={toggleActive} onSwitch={switchRole} onRemove={remove} onTransfer={transfer} />) : <p className="text-[14px] text-mute">Aucun compte pro.</p>}</div>
        </>
      )}
    </div>
  );
}
